import Breadcrumb from "@/components/base/Breadcrumb";
import SectionHeading from "@/components/base/SectionHeading";
import BackToTop from "@/components/feature/BackToTop";
import PageMeta from "@/components/feature/PageMeta";
import PrinciplesGrid from "@/components/feature/PrinciplesGrid";
import SiteFooter from "@/components/feature/SiteFooter";
import SiteHeader from "@/components/feature/SiteHeader";
import VoicesGrid from "@/components/feature/VoicesGrid";
import { aboutStats, philosophy, positioning } from "@/mocks/about";
import { contactChannels, siteInfo } from "@/mocks/site";
import ContactForm from "./components/ContactForm";

const aboutJsonLd = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "AboutPage",
      name: `关于 ${siteInfo.brand}`,
      url: "https://rnmka.cn/about",
      description: siteInfo.intro,
      inLanguage: "zh-CN",
    },
    {
      "@type": "Organization",
      name: siteInfo.brand,
      url: "https://rnmka.cn/",
      email: siteInfo.email,
      description: siteInfo.intro,
      contactPoint: {
        "@type": "ContactPoint",
        contactType: "内容合作与线索投稿",
        email: siteInfo.email,
        availableLanguage: ["zh-CN"],
      },
    },
    {
      "@type": "BreadcrumbList",
      itemListElement: [
        {
          "@type": "ListItem",
          position: 1,
          name: "首页",
          item: "https://rnmka.cn/",
        },
        {
          "@type": "ListItem",
          position: 2,
          name: "关于我们",
          item: "https://rnmka.cn/about",
        },
      ],
    },
  ],
};

export default function AboutPage() {
  return (
    <div className="min-h-screen bg-background-50">
      <PageMeta
        title={`关于我们｜爆料看料围观站 - ${siteInfo.brand}`}
        description={`了解${siteInfo.brand}的品牌故事、网站定位与内容原则。我们专注爆料看料与吃瓜围观，用有条理的内容帮你把热点看明白，坚持不搬运传闻、不针对个人定性。`}
        keywords="关于我们,爆料看料,爆料看料围观,吃瓜,内容原则"
        path="/about"
        jsonLd={aboutJsonLd}
      />
      <SiteHeader />

      <main>
        <section className="bg-background-100 pt-28 md:pt-32 pb-12 md:pb-14">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "关于我们" }]} />
            <h1 className="mt-6 font-heading text-3xl md:text-5xl font-bold text-foreground-950">
              关于 {siteInfo.brand}
            </h1>
            <p className="mt-4 text-sm md:text-base text-foreground-600 leading-relaxed max-w-2xl">
              {siteInfo.intro}
            </p>
            <dl className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-5 max-w-3xl">
              {aboutStats.map((stat) => (
                <div
                  key={stat.label}
                  className="rounded-lg bg-background-50 border border-background-200/80 p-4"
                >
                  <dt className="font-heading text-xl md:text-2xl font-bold text-primary-600">
                    {stat.value}
                  </dt>
                  <dd className="mt-1 text-xs md:text-sm text-foreground-500 font-label">
                    {stat.label}
                  </dd>
                </div>
              ))}
            </dl>
          </div>
        </section>

        <section className="py-14 md:py-20">
          <div className="max-w-7xl mx-auto px-4 md:px-6 grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-14 items-center">
            <div className="w-full h-64 md:h-[420px] rounded-lg overflow-hidden bg-background-200">
              <img
                src="https://readdy.ai/api/search-image?query=abstract%20flat%20vector%20illustration%20of%20a%20cozy%20reading%20community%2C%20tiny%20people%20sitting%20around%20with%20screens%20notes%20and%20coffee%2C%20warm%20coral%20amber%20and%20sage%20green%20palette%2C%20soft%20grain%20texture%2C%20editorial%20magazine%20style%2C%20clean%20minimal%20composition&width=1000&height=800&seq=gua-about-community&orientation=landscape"
                alt="读者社区围坐一起看屏幕与笔记的抽象插画"
                title="热瓜前线 读者社区"
                className="w-full h-full object-cover object-top"
              />
            </div>
            <div>
              <SectionHeading
                kicker="品牌介绍"
                title="我们是谁"
                desc="一群喜欢看热闹、也愿意把热闹看明白的人。"
              />
              <p className="mt-5 text-sm md:text-base text-foreground-600 leading-relaxed">
                {siteInfo.brand}从 {siteInfo.since} 年开始做这件事，起因很简单：每次热点刷屏，评论区里总有人问「到底发生了什么」，却很少有人愿意把前因后果讲清楚。于是我们决定自己来整理。
              </p>
              <p className="mt-4 text-sm md:text-base text-foreground-600 leading-relaxed">
                我们把站点定位成一座「围观看台」：你不需要从第一分钟就盯着，也能在几分钟内了解一件事的来龙去脉。这里没有煽动性的标题，只有尽量完整的信息和克制的表达。
              </p>
              <ul className="mt-6 flex flex-col gap-3">
                {positioning.map((item) => (
                  <li
                    key={item}
                    className="flex items-start gap-2.5 text-sm md:text-base text-foreground-700"
                  >
                    <i className="ri-check-double-line w-5 h-5 flex items-center justify-center text-accent-600 shrink-0"></i>
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>

        <section className="bg-background-100 py-14 md:py-20">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <SectionHeading
              kicker="发展理念"
              title="我们怎么把一件事讲清楚"
              desc="从一条模糊的线索，到一篇能读的观察笔记，中间要走完这三步。"
            />
            <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-5">
              {philosophy.map((item, index) => (
                <div
                  key={item.title}
                  className="h-full rounded-lg bg-background-50 border border-background-200/80 p-6"
                >
                  <div className="flex items-center justify-between">
                    <span className="w-11 h-11 flex items-center justify-center rounded-lg bg-primary-100 text-primary-700">
                      <i className={`${item.icon} text-xl`}></i>
                    </span>
                    <span className="font-heading text-3xl font-black text-background-300">
                      0{index + 1}
                    </span>
                  </div>
                  <h3 className="mt-4 font-heading font-bold text-lg text-foreground-900">
                    {item.title}
                  </h3>
                  <p className="mt-2 text-sm text-foreground-600 leading-relaxed">
                    {item.desc}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section id="principles" className="py-14 md:py-20 scroll-mt-20">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <SectionHeading
              kicker="内容原则"
              title="热闹可以看，底线不能丢"
              desc="围观是一种乐趣，也是一种责任。这四条是我们在整理每一篇内容时都会遵守的原则。"
            />
            <div className="mt-8">
              <PrinciplesGrid />
            </div>
          </div>
        </section>

        <section id="voices" className="bg-background-100 py-14 md:py-20 scroll-mt-20">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <SectionHeading
              kicker="用户口碑"
              title="他们这样围观"
              desc="来自长期蹲守在这里的读者反馈。我们更在意的是，大家看完之后是不是真的更明白了一点。"
            />
            <div className="mt-8">
              <VoicesGrid />
            </div>
          </div>
        </section>

        <section id="contact" className="py-14 md:py-20 scroll-mt-20">
          <div className="max-w-7xl mx-auto px-4 md:px-6 grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-14">
            <div>
              <SectionHeading
                kicker="联系方式"
                title="有话想说，随时找我们"
                desc="无论是内容合作、线索投稿还是纠错建议，都欢迎直接联系我们。"
              />
              <div className="mt-6 flex flex-col gap-4">
                {contactChannels.map((item) => (
                  <div
                    key={item.value}
                    className="flex items-start gap-4 rounded-lg bg-background-100 border border-background-200/80 p-4"
                  >
                    <span className="w-10 h-10 shrink-0 flex items-center justify-center rounded-lg bg-accent-100 text-accent-700">
                      <i className={`${item.icon} text-lg`}></i>
                    </span>
                    <span className="flex flex-col">
                      <span className="font-label text-sm font-medium text-foreground-900">
                        {item.label}
                      </span>
                      <span className="mt-0.5 text-sm text-foreground-600 break-all">
                        {item.value}
                      </span>
                    </span>
                  </div>
                ))}
              </div>
              <p className="mt-5 text-xs text-foreground-500 font-label leading-relaxed">
                我们通常会在 1-2 个工作日内回复。投稿时请尽量附上可查证的公开信息来源，这能大幅提高被采纳的概率。
              </p>
            </div>

            <div className="rounded-lg bg-background-100 border border-background-200/80 p-5 md:p-6">
              <h3 className="font-heading font-bold text-lg text-foreground-900">
                给编辑部留言
              </h3>
              <p className="mt-1.5 text-sm text-foreground-600">
                填写下面的表单，内容会直接送到编辑的收件箱。
              </p>
              <div className="mt-5">
                <ContactForm />
              </div>
            </div>
          </div>
        </section>
      </main>

      <SiteFooter />
      <BackToTop />
    </div>
  );
}